import { useMemo } from "react"

import { MAIN_PORTAL_INSTANCE_ID } from "@productlane/lib/src/portal-instance"
import { mutators } from "@productlane/zero/mutators"

import { portalColorSeeds } from "@/lib/portal-instances"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"

export interface PortalEntry {
	id: string
	name: string
	logoUrl: string | null
	colorSeed: string | null
	/** Why customers on this portal still can't see a checked item. */
	blocker: string | null
}

/**
 * Main first, then the named portals by name, the same order as the portal
 * list on the feature requests screen. Empty when the workspace has one portal.
 */
export function usePortalEntries() {
	const workspace = useZeroWorkspace()
	const zero = useZero()
	const instances = workspace?.portalInstances

	const entries = useMemo<PortalEntry[]>(() => {
		if (!workspace || !instances || instances.length === 0) return []
		const allOff = !workspace.domainPublic
		const seeds = portalColorSeeds(instances)
		const named = [...instances].sort((a, b) => a.name.localeCompare(b.name))
		return [
			{
				id: MAIN_PORTAL_INSTANCE_ID,
				name: "Main portal",
				logoUrl: workspace.logoUrl ?? null,
				colorSeed: seeds.get(null) ?? null,
				blocker: allOff ? "Feature requests are off on every portal" : null,
			},
			...named.map((instance) => ({
				id: instance.id,
				name: instance.name,
				// No workspace logo fallback: coloured initials tell portals apart.
				logoUrl: instance.logoUrl ?? null,
				colorSeed: seeds.get(instance.id) ?? null,
				blocker: allOff
					? "Feature requests are off on every portal"
					: instance.showRoadmap
						? null
						: "Feature requests are off here",
			})),
		]
	}, [workspace, instances])

	const namedPortalIds = useMemo(
		() =>
			entries.filter((e) => e.id !== MAIN_PORTAL_INSTANCE_ID).map((e) => e.id),
		[entries],
	)

	// The Main switch still gates every portal, so turning it on is the fix for
	// all of them; a named portal with its own page off needs only that page.
	const fixBlocker = async (portalId: string) => {
		if (!workspace) return
		if (!workspace.domainPublic) {
			await mutateOk(
				zero.mutate(
					mutators.workspaces.update({ id: workspace.id, domainPublic: true }),
				),
			)
			return
		}
		if (portalId === MAIN_PORTAL_INSTANCE_ID) return
		await mutateOk(
			zero.mutate(
				mutators.portalInstances.update({ id: portalId, showRoadmap: true }),
			),
		)
	}

	return { entries, namedPortalIds, fixBlocker }
}
